import type { SlotComponentProps } from '@mui/base';
import type { IconButtonProps, SxProps } from '@mui/material';
import { IconButton } from '@mui/material';
import type { DesktopDateTimePickerProps } from '@mui/x-date-pickers';
import {
	DesktopDatePicker,
	DesktopDateTimePicker,
	LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import type { DateTime } from 'luxon';
import type { ComponentType, ElementType } from 'react';

import IconCalendarEvent from '~icons/bx/calendar-event';

import { theme } from '@adflow/frontend/ui-theme';

import type { Selector } from '../types';

const styles = {
	input: ({ disabled, readOnly, archived, error }: Partial<Selector>) => ({
		width: '100%',

		'& .MuiInputBase-root': {
			color: disabled ? theme.colors.text.disabled : theme.colors.text.primary,
			fontStyle: 'normal',
			backgroundColor:
				disabled && !readOnly
					? theme.colors.input.disabledBackground
					: archived
						? theme.colors.background.paperTransparent
						: readOnly
							? theme.colors.background.fullTransparent
							: 'white',
		},

		'& .MuiOutlinedInput-notchedOutline': {
			borderRadius: '2px',
			border: readOnly
				? 'none'
				: `1px solid ${
						error
							? theme.colors.palettes.error.main
							: disabled
								? theme.colors.input.disabledBorder
								: theme.colors.input.border
					} !important`,
		},

		'& .MuiInputBase-input': {
			minWidth: 0,
			padding: '0.5435rem 0 0.5435rem 0.75rem',
			WebkitTextFillColor: 'unset !important',
		},

		'& .MuiInputAdornment-root': {
			marginLeft: 0,
		},
	}),
	calendar: {
		'& .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root': {
			fontSize: '0.85rem',
		},

		'& .MuiMultiSectionDigitalClockSection-root:after': {
			// Don't allow scrolling past the end of the hours, minutes, or AM/PM lists
			display: 'none',
		},
	},
} satisfies Record<string, SxProps | (() => SxProps)>;

export const dateTimeStyles = styles;

export type SelectorDateTimeValidationProps = Pick<
	DesktopDateTimePickerProps<DateTime>,
	| 'disableFuture'
	| 'disablePast'
	| 'shouldDisableDate'
	| 'shouldDisableMonth'
	| 'shouldDisableYear'
	| 'minDate'
	| 'maxDate'
	| 'minutesStep'
	| 'minTime'
	| 'maxTime'
	| 'shouldDisableTime'
>;

export interface SelectorDateTimeProps
	extends
		Omit<Selector, 'items' | 'dropdownMaxHeight' | 'placeholder' | 'archived'>,
		Pick<Required<DesktopDateTimePickerProps<DateTime>>, 'value' | 'onChange'>,
		Pick<DesktopDateTimePickerProps<DateTime>, 'onAccept' | 'onError'>,
		SelectorDateTimeValidationProps {
	archived?: boolean;
	clearable?: boolean;
	dateOnly?: boolean;
	/**
	 * MUI's date picker doesn't have a placeholder prop, so we have to fake it.
	 *
	 * This date will be used as the placeholder if the date picker is disabled.
	 */
	disabledPlaceholderDate?: DateTime;
	actionBar?: ElementType;
	actionBarProps?: SlotComponentProps<ComponentType<unknown>, unknown, unknown>;
}

export function SelectorDateTime(props: SelectorDateTimeProps) {
	const {
		// Selector props
		error,
		disabled = false,
		readOnly = false,
		testid,
		inputRef,

		// DesktopDateTimePickerProps
		value,
		onChange,
		onAccept,
		onError,

		// SelectorDateTimeProps
		archived,
		clearable,
		dateOnly,
		disabledPlaceholderDate,
		actionBar,
		actionBarProps,

		...validationProps
	} = props;

	const Component = dateOnly ? DesktopDatePicker : DesktopDateTimePicker;

	return (
		<LocalizationProvider dateAdapter={AdapterLuxon}>
			<Component
				disabled={disabled}
				readOnly={readOnly}
				inputRef={inputRef}
				value={
					value ??
					(disabledPlaceholderDate && disabled ? disabledPlaceholderDate : null)
				}
				onChange={onChange}
				onAccept={onAccept}
				onError={onError}
				{...validationProps}
				slots={{
					openPickerButton: CalendarEventIconButton,
					// @ts-expect-error - Getting these types right is hard
					actionBar,
				}}
				slotProps={{
					actionBar: actionBarProps,
					field: {
						clearable,
						inputProps: {
							// @ts-expect-error - MUI doesn't have a testid prop
							'data-testid': testid ? `${testid}-input` : undefined,
						},
					},
					openPickerButton: {
						disabled,
						readOnly,
					} as SlotComponentProps<
						typeof CalendarEventIconButton,
						unknown,
						unknown
					>,
					desktopPaper: {
						sx: styles.calendar,
					},
				}}
				sx={styles.input({ disabled, readOnly, archived, error })}
			/>
		</LocalizationProvider>
	);
}

interface CalendarEventIconButtonProps
	extends IconButtonProps, Pick<Selector, 'disabled' | 'readOnly'> {}

function CalendarEventIconButton({
	disabled,
	readOnly,
	...props
}: CalendarEventIconButtonProps) {
	if (readOnly) {
		return null;
	}

	return (
		<IconButton {...props} disabled={disabled}>
			<IconCalendarEvent
				style={{
					width: '1em',
					height: '1em',
					color: disabled
						? theme.colors.text.faint
						: theme.colors.input.adornment,
				}}
			/>
		</IconButton>
	);
}
